import { useState, useEffect } from 'react';
import { defaultQuestionsByType, HelpType, performanceQuestionsByRole, onboardingQuestionsByPermission, onboardingGreetingByPermission } from './products';
import rivalAiIcon from "./icons/rival-ai-icon.svg";
import rivalRosiWorkflowIcon from "./icons/rosi-workflow.svg";
import rivalRosiLearnIcon from "./icons/rosi-learn.svg";
import rivalRosiPerformIcon from "./icons/rosi-perform.svg";
import rivalRosiRecruitIcon from "./icons/rosi-recruit.svg";

export const usePersistentState = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
}

export const formatSource = (source) => {
  const url = new URL(source);
  const subdomain = url.hostname.split('.')[0];

  const pathSegments = url.pathname.split('/').filter(Boolean);
  const page = pathSegments[pathSegments.length - 1]?.replace(/\.htm$/, '');

  const hashParts = url.hash.split('/');
  const hashPath = hashParts.length > 2 ? hashParts[2] : undefined;

  const path = page || hashPath;

  if (subdomain.includes('help') && path) {
    const linkText = path.match(/^\d+$/)
      ? `Knowledge Base [#${path}]`
      : `Knowledge Base [${path.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}]`;
    return `Source: <a href="${source}" target="_blank" class="tw-no-underline hover:tw-underline tw-text-berthablue">${linkText}</a>`;
  }
  return `Source: ${source}`;
};

export const INPUT_CHARS_LIMIT = 1000
export const PERFORMANCE_CHARS_LIMIT = 2000
export const CHAR_WARNING_THRESHOLD = 30
export const PERFORMANCE_ADMIN_ROLES = ['administrator', 'clientadministrator', 'hradministrator', 'successionplanningadministrator'];

export const getDefaultQuestions = (helpType, userRoles, rosiPermissionLevel, managerReviewWriterEnabled) => {
  if (helpType === HelpType.ONBOARDING) {
    const isAdmin = rosiPermissionLevel.toLowerCase().includes('admin');
    return isAdmin ? onboardingQuestionsByPermission.admin : onboardingQuestionsByPermission.basic;
  }
  
  if (helpType === HelpType.PERFORM) {
    if (!userRoles) {
      return performanceQuestionsByRole.basic(managerReviewWriterEnabled);
    }
    
    const roles = userRoles.toLowerCase().split(' ');
    const isAdmin = roles.some(role => PERFORMANCE_ADMIN_ROLES.includes(role));
    
    if (isAdmin) return performanceQuestionsByRole.admin;
    if (roles.includes('manager')) return performanceQuestionsByRole.manager(managerReviewWriterEnabled);
    return performanceQuestionsByRole.basic(managerReviewWriterEnabled);
  }
  
  return defaultQuestionsByType[helpType];
};

export const getGreetingMessage = (helpType, rosiPermissionLevel, greetingMessagesByType) => {
  if (helpType === HelpType.ONBOARDING) {
    const isAdmin = rosiPermissionLevel.toLowerCase().includes('admin');
    return isAdmin ? onboardingGreetingByPermission.admin : onboardingGreetingByPermission.basic;
  }
  
  return greetingMessagesByType[helpType];
};

/**
 * Formats text by converting markdown bold syntax to HTML.
 * Note: This function intentionally does not sanitize HTML because
 * all content comes from our trusted backend API, not user input.
 */
export const formatText = (text) => {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

export const getIconByHelpType = (type) => {
  switch (type) {
    case HelpType.ONBOARDING:
      return rivalRosiWorkflowIcon;
    case HelpType.LEARNING:
      return rivalRosiLearnIcon;
    case HelpType.PERFORM:
      return rivalRosiPerformIcon;
    case HelpType.RECRUITING:
      return rivalRosiRecruitIcon;
    default:
      return rivalAiIcon;
  }
};
